import { LogoLoader, logoLoaderWaitInProgress } from '@flashka/shared/ui';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DotBackground from '@/components/ui/dot-background';
import { cn } from '@/lib/utils';

/**
 * How long an `area` loader that CONTINUES a wait holds the whole screen: the
 * hand-over, not the wait. Covering for the whole wait trapped the user — an
 * offline study-group deck hid the sidebar and top bar for good, the invite
 * page hid its own preview. After this the cover fades off the loader that
 * sits in the page's own area, under the chrome.
 */
const CONTINUE_COVER_MS = 1200;

/**
 * The page-load wait: the shuffling Flashka mark (shared `LogoLoader`), with
 * the app's translated accessible name.
 *
 * - `screen` covers the viewport — the app-shell, auth and sync holds, where
 *   nothing else is on screen yet.
 * - `area` (default) centres the mark in the space the page would fill, under
 *   any chrome that is already up (sidebar, header).
 * Both sit on the dotted canvas (`DotBackground`), like every page.
 *
 * `label` (pre-translated, no trailing dots: the loader animates its own) only
 * where the wait needs explaining — a bare mark is right for most page loads.
 */
export function PageLoader({
  label,
  variant = 'area',
  className,
  testID,
}: {
  label?: string;
  variant?: 'screen' | 'area';
  className?: string;
  testID?: string;
}) {
  const { t } = useTranslation();
  // A loader that CONTINUES a wait already on screen (boot → shell → session
  // → sync → route chunk → page gate) first covers the whole screen, so the
  // mark stays exactly where the last one had it — for the hand-over only
  // (CONTINUE_COVER_MS). Decided once, at mount.
  const [continuing] = useState(logoLoaderWaitInProgress);
  const [covering, setCovering] = useState(continuing && variant === 'area');
  // Unmounted once faded: an invisible mark would still count as a wait.
  const [coverGone, setCoverGone] = useState(!covering);
  useEffect(() => {
    if (!covering) return;
    const timer = window.setTimeout(() => setCovering(false), CONTINUE_COVER_MS);
    return () => window.clearTimeout(timer);
  }, [covering]);
  const mark = (id?: string) => (
    // Always on the dotted canvas: the boot loader in index.html draws the same
    // ground, so a wait that hands over from it never changes background.
    <DotBackground isCanvas fixedDots>
      <LogoLoader
        label={label}
        accessibilityLabel={t('pageLoader.loading')}
        testID={id}
      />
    </DotBackground>
  );
  if (variant === 'screen') {
    return (
      <div className={cn('fixed inset-0 z-[9000]', className)}>
        {mark(testID ?? 'page-loader')}
      </div>
    );
  }
  return (
    <div className={cn('flex h-full min-h-[40vh] w-full flex-1', className)}>
      {mark(testID ?? 'page-loader')}
      {/* The hand-over cover: the mark stays where the last loader had it,
          then fades to the one in this area (both on the same page clock). */}
      {coverGone ? null : (
        <div
          aria-hidden
          onTransitionEnd={() => {
            if (!covering) setCoverGone(true);
          }}
          className={cn(
            'pointer-events-none fixed inset-0 z-[9000] transition-opacity duration-300',
            covering ? 'opacity-100' : 'opacity-0',
          )}>
          {mark()}
        </div>
      )}
    </div>
  );
}
